import React from 'react';
import { useTranslation } from 'react-i18next';
import ContactUs from '../components/contactUs/ContactUs';
import './ThreeDPrinting.css';

const content = {
  sv: {
    title: '3D-printing & prototyper | THAS Multitech',
    description: 'Gör din idé till en fysisk del med THAS Multitech. Vi hjälper dig med 3D-printing, prototyper och anpassade detaljer. Berätta om ditt projekt och be om offert.',
    eyebrow: '3D-printing hos THAS Multitech',
    heading: 'Din idé.',
    headingAccent: 'I en ny dimension.',
    introduction: 'Från första skiss till en del du kan hålla i. Vi hjälper dig att utforska, utveckla och förverkliga ditt projekt med 3D-printing.',
    quote: 'Berätta om din idé',
    explore: 'Upptäck möjligheterna',
    heroAlt: 'Illustration av en modern 3D-skrivare med en detalj under tillverkning',
    imageNote: 'Illustrationsbild',
    heroCaption: 'Små detaljer. Nya möjligheter.',
    tagOne: 'Prototyper',
    tagTwo: 'Anpassade detaljer',
    tagThree: 'Hjälp med design',
    applicationsEyebrow: 'Vad vill du skapa?',
    applicationsTitle: 'En idé kan ta många former.',
    applicationsIntro: 'En lösning på ett vardagsproblem, en modell att testa eller nästa steg i din produktutveckling. Vi börjar med ditt behov.',
    applications: [
      { icon: 'prototype', title: 'Prototyper & modeller', text: 'Gör en idé fysisk. Utforska form, storlek och funktion innan du tar projektet vidare.' },
      { icon: 'enclosure', title: 'Kapslingar & hållare', text: 'Utforska en anpassad kapsling för elektronik, en praktisk hållare eller ett fäste för ditt projekt.' },
      { icon: 'parts', title: 'Anpassade detaljer', text: 'Behöver du en del med en särskild form eller ett särskilt mått? Beskriv hur den ska användas, så tittar vi på möjligheterna.' },
      { icon: 'design', title: 'Från skiss till 3D', text: 'Du behöver inte ha en färdig modell. Vi går igenom din idé och diskuterar vilken hjälp med design som behövs.' },
    ],
    processEyebrow: 'Så går det till',
    processTitle: 'Från ”tänk om” till nästa steg.',
    processIntro: 'Vi kommer överens om förutsättningarna innan produktionen börjar.',
    steps: [
      { title: 'Visa oss din idé', text: 'Skicka en beskrivning, skiss eller länk till din 3D-fil. Berätta gärna om mått och antal.' },
      { title: 'Vi går igenom behoven', text: 'Tillsammans diskuterar vi användning, utformning, material och vad som är möjligt att tillverka.' },
      { title: 'Du får en offert', text: 'Vi återkommer med pris och föreslagen leveranstid utifrån just ditt projekt.' },
      { title: 'Dags att tillverka', text: 'När du har godkänt offerten och detaljerna planerar vi produktionen och leveransen.' },
    ],
    briefEyebrow: 'En bra start',
    briefTitle: 'Ingen färdig ritning? Börja med en fråga.',
    briefText: 'Berätta vad du vill lösa. En kort beskrivning räcker för att starta samtalet – vi hjälper dig att reda ut nästa steg.',
    briefList: ['Vad ska delen användas till?', 'Ungefärliga mått och önskat antal', 'En skiss, bild eller 3D-fil om du har en', 'När du skulle vilja ha den klar'],
    questionsEyebrow: 'Bra att veta',
    questionsTitle: 'Vanliga frågor om 3D-printing',
    questions: [
      { question: 'Behöver jag ha en färdig 3D-fil?', answer: 'Nej. Du kan börja med en idé, en skiss eller ett foto. Vi går igenom underlaget tillsammans och diskuterar om modellering eller annan designhjälp behövs.' },
      { question: 'Vilka material och storlekar går att skriva ut?', answer: 'Det beror på delens utformning och hur den ska användas. Beskriv mått, belastning och miljö i din förfrågan, så kan vi diskutera materialval och tillverkningsmöjligheter för ditt projekt.' },
      { question: 'Vad kostar det, och hur lång tid tar det?', answer: 'Pris och leveranstid beror bland annat på storlek, antal, material och hur mycket förberedelse som behövs. Vi lämnar en offert och kommer överens om tidsplanen innan vi sätter igång.' },
      { question: 'Hur skickar jag en ritning eller 3D-fil?', answer: 'Lägg en delningslänk i ditt meddelande och kontrollera att vi har behörighet att öppna den. Du kan också mejla underlaget till info@thas-multitech.com. Kontaktformuläret tar inte emot bilagor.' },
      { question: 'Kan jag beställa en enda del?', answer: 'Du är välkommen med en förfrågan om både enstaka delar och flera exemplar. Beskriv ditt önskade antal, så bedömer vi en lämplig lösning och lämnar en offert.' },
    ],
    contactEyebrow: 'Låt oss ta nästa steg',
    contactTitle: 'En fråga idag. En möjlighet imorgon.',
    contactIntro: 'Be om en offert eller ställ en fråga om 3D-printing. Berätta så mycket eller så lite du vet om din idé.',
    emailLabel: 'Vill du skicka bilder eller filer?',
    emailLink: 'Mejla ditt underlag',
    emailSubject: 'Förfrågan om 3D-printing',
  },
  en: {
    title: '3D Printing & Prototypes | THAS Multitech',
    description: 'Turn your idea into a physical part with THAS Multitech. Explore 3D printing, prototypes and custom parts. Tell us about your project and request a quote.',
    eyebrow: '3D printing at THAS Multitech',
    heading: 'Your idea.',
    headingAccent: 'A new dimension.',
    introduction: 'From an initial sketch to something you can hold. We help you explore, develop and bring your project to life with 3D printing.',
    quote: 'Tell us about your idea',
    explore: 'Explore the possibilities',
    heroAlt: 'Illustration of a modern 3D printer producing a component',
    imageNote: 'Illustrative image',
    heroCaption: 'Small details. New possibilities.',
    tagOne: 'Prototypes',
    tagTwo: 'Custom parts',
    tagThree: 'Design support',
    applicationsEyebrow: 'What would you like to create?',
    applicationsTitle: 'An idea can take many forms.',
    applicationsIntro: 'A solution to an everyday problem, a model to test or the next step in developing your product. We start with what you need.',
    applications: [
      { icon: 'prototype', title: 'Prototypes & models', text: 'Make an idea tangible. Explore its shape, size and function before moving your project forward.' },
      { icon: 'enclosure', title: 'Enclosures & holders', text: 'Explore a custom enclosure for electronics, a practical holder or a mounting bracket for your project.' },
      { icon: 'parts', title: 'Custom parts', text: 'Need a part with a particular shape or size? Tell us how it will be used and we will explore the possibilities.' },
      { icon: 'design', title: 'From sketch to 3D', text: 'You do not need a finished model. We review your idea and discuss the design support your project needs.' },
    ],
    processEyebrow: 'How it works',
    processTitle: 'From “what if” to what comes next.',
    processIntro: 'We agree on the details before production begins.',
    steps: [
      { title: 'Show us your idea', text: 'Send a description, sketch or link to your 3D file. Include dimensions and quantities if you have them.' },
      { title: 'Discuss the requirements', text: 'Together, we review the intended use, design, materials and manufacturing possibilities.' },
      { title: 'Receive a quote', text: 'We provide a price and proposed lead time based on your particular project.' },
      { title: 'Time to make it', text: 'Once you have approved the quote and details, we plan production and delivery.' },
    ],
    briefEyebrow: 'A good place to start',
    briefTitle: 'No finished drawing? Start with a question.',
    briefText: 'Tell us what you want to solve. A short description is enough to start a conversation — we will help you work out the next step.',
    briefList: ['What will the part be used for?', 'Approximate dimensions and quantity', 'A sketch, photo or 3D file if you have one', 'When you would like it to be ready'],
    questionsEyebrow: 'Good to know',
    questionsTitle: 'Your 3D printing questions',
    questions: [
      { question: 'Do I need a finished 3D file?', answer: 'No. You can start with an idea, sketch or photo. We review it together and discuss whether modelling or other design support is needed.' },
      { question: 'What materials and sizes can you print?', answer: 'That depends on the design and intended use of the part. Tell us about the dimensions, loads and environment so we can discuss material choices and manufacturing possibilities for your project.' },
      { question: 'How much does it cost, and how long does it take?', answer: 'Price and lead time depend on factors including size, quantity, material and the preparation required. We provide a quote and agree on a schedule before starting.' },
      { question: 'How do I send a drawing or 3D file?', answer: 'Include a sharing link in your message and check that we have permission to open it. You can also email your files to info@thas-multitech.com. The contact form does not accept attachments.' },
      { question: 'Can I request just one part?', answer: 'You are welcome to enquire about a single part or multiple copies. Tell us the quantity you need so we can assess a suitable solution and provide a quote.' },
    ],
    contactEyebrow: 'Let’s take the next step',
    contactTitle: 'A question today. A possibility tomorrow.',
    contactIntro: 'Request a quote or ask us about 3D printing. Share as much or as little as you know about your idea.',
    emailLabel: 'Have pictures or files to share?',
    emailLink: 'Email your project files',
    emailSubject: '3D printing enquiry',
  },
};

function PrintingIcon({ type }) {
  const paths = {
    prototype: <><path d="m12 3 9 5v10l-9 5-9-5V8l9-5Z" /><path d="m3 8 9 5 9-5M12 13v10M7.5 5.5l9 5" /></>,
    enclosure: <><rect x="3" y="5" width="18" height="15" rx="3" /><path d="M3 10h18M7 15h2m3 0h5M7 5V3m10 2V3" /></>,
    parts: <><path d="M8 3h8v5h5v8h-5v5H8v-5H3V8h5V3Z" /><circle cx="12" cy="12" r="2.5" /></>,
    design: <><path d="m15 4 5 5M4 20l5-1L21 7a2.2 2.2 0 0 0-5-3L4 16v4Z" /><path d="M12 4H4v8m8 8h8v-8" /></>,
  };
  return <svg viewBox="0 0 24 26" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[type]}</svg>;
}

function Arrow() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true"><path d="M4 12h15m-6-6 6 6-6 6" /></svg>;
}

export default function ThreeDPrinting() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || 'sv').startsWith('en') ? 'en' : 'sv';
  const copy = content[language];

  return (
    <main className="printing-page">

      <section className="printing-hero" aria-labelledby="printing-heading">
        <div className="printing-shell printing-hero-grid">
          <div className="printing-hero-copy">
            <p className="printing-eyebrow"><span aria-hidden="true" />{copy.eyebrow}</p>
            <h1 id="printing-heading">{copy.heading}<br /><span>{copy.headingAccent}</span></h1>
            <p className="printing-hero-intro">{copy.introduction}</p>
            <div className="printing-actions">
              <a className="printing-button" href="#printing-contact">{copy.quote}<Arrow /></a>
              <a className="printing-text-link" href="#printing-possibilities">{copy.explore}<span aria-hidden="true">↓</span></a>
            </div>
            <ul className="printing-tags" aria-label={language === 'en' ? '3D printing services' : 'Tjänster inom 3D-printing'}>
              <li>{copy.tagOne}</li><li>{copy.tagTwo}</li><li>{copy.tagThree}</li>
            </ul>
          </div>
          <figure className="printing-hero-visual">
            <img src="/images/3d-printing-workshop.webp" srcSet="/images/3d-printing-workshop-640.webp 640w, /images/3d-printing-workshop.webp 1400w" sizes="(max-width: 460px) calc(100vw - 32px), (max-width: 760px) calc(100vw - 40px), (max-width: 1100px) calc(49vw - 42px), (max-width: 1280px) calc(49vw - 66px), 559px" alt={copy.heroAlt} width="1400" height="788" fetchpriority="high" />
            <figcaption><span>{copy.heroCaption}</span><small>{copy.imageNote}</small></figcaption>
          </figure>
        </div>
      </section>

      <section className="printing-section printing-shell" id="printing-possibilities" aria-labelledby="printing-possibilities-heading">
        <div className="printing-section-heading">
          <p className="printing-eyebrow">{copy.applicationsEyebrow}</p>
          <h2 id="printing-possibilities-heading">{copy.applicationsTitle}</h2>
          <p>{copy.applicationsIntro}</p>
        </div>
        <div className="printing-applications">
          {copy.applications.map((item) => (
            <article className="printing-application" key={item.icon}>
              <span className="printing-icon"><PrintingIcon type={item.icon} /></span>
              <h3>{item.title}</h3><p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="printing-process printing-section" aria-labelledby="printing-process-heading">
        <div className="printing-shell">
          <div className="printing-section-heading">
            <p className="printing-eyebrow">{copy.processEyebrow}</p>
            <h2 id="printing-process-heading">{copy.processTitle}</h2>
            <p>{copy.processIntro}</p>
          </div>
          <ol className="printing-steps">
            {copy.steps.map((step, index) => (
              <li key={step.title}>
                <span className="printing-step-number" aria-hidden="true">0{index + 1}</span>
                <h3>{step.title}</h3><p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="printing-section printing-shell" aria-labelledby="printing-brief-heading">
        <div className="printing-brief">
          <div>
            <p className="printing-eyebrow">{copy.briefEyebrow}</p>
            <h2 id="printing-brief-heading">{copy.briefTitle}</h2>
            <p>{copy.briefText}</p>
            <a className="printing-button printing-button-dark" href="#printing-contact">{copy.quote}<Arrow /></a>
          </div>
          <ul>
            {copy.briefList.map((item) => <li key={item}><span aria-hidden="true">✓</span>{item}</li>)}
          </ul>
        </div>
      </section>

      <section className="printing-faq printing-shell" aria-labelledby="printing-faq-heading">
        <div className="printing-section-heading">
          <p className="printing-eyebrow">{copy.questionsEyebrow}</p>
          <h2 id="printing-faq-heading">{copy.questionsTitle}</h2>
        </div>
        <div className="printing-questions">
          {copy.questions.map((item) => (
            <details key={item.question}>
              <summary>{item.question}<span aria-hidden="true">+</span></summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="printing-contact" id="printing-contact" aria-labelledby="printing-contact-heading">
        <div className="printing-shell printing-section-heading">
          <p className="printing-eyebrow">{copy.contactEyebrow}</p>
          <h2 id="printing-contact-heading">{copy.contactTitle}</h2>
          <p>{copy.contactIntro}</p>
          <p className="printing-email-note">{copy.emailLabel} <a href={`mailto:info@thas-multitech.com?subject=${encodeURIComponent(copy.emailSubject)}`}>{copy.emailLink}<span aria-hidden="true"> ↗</span></a></p>
        </div>
        <ContactUs defaultService="3d-printing" />
      </section>
    </main>
  );
}
